import { useState } from 'react'
import { Clock, Check, Edit3, Sparkles, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { OpinionSessionTurnDetail } from '../../services/api'

interface SessionTimelineProps {
  turns: OpinionSessionTurnDetail[]
  currentVersion: number
  query: string
}

function formatDate(dateString: string) {
  try {
    return new Date(dateString).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

function getTypeLabel(type: string): { label: string; icon: typeof Sparkles; color: string } {
  switch (type) {
    case 'Generation':
      return { label: 'Generacja', icon: Sparkles, color: 'text-blue-600' }
    case 'Revision':
      return { label: 'Rewizja', icon: Edit3, color: 'text-orange-600' }
    case 'Accepted':
      return { label: 'Zaakceptowana', icon: Check, color: 'text-green-600' }
    default:
      return { label: type, icon: Sparkles, color: 'text-gray-600' }
  }
}

function parseSources(sourcesUsed: string | undefined): { eli: number; eurlex: number; caseLaw: number; total: number } {
  if (!sourcesUsed) return { eli: 0, eurlex: 0, caseLaw: 0, total: 0 }
  try {
    const sources = JSON.parse(sourcesUsed)
    if (!Array.isArray(sources)) return { eli: 0, eurlex: 0, caseLaw: 0, total: 0 }
    const eli = sources.filter((s: any) => s.type === 'eli').length
    const eurlex = sources.filter((s: any) => s.type === 'eurlex').length
    const caseLaw = sources.filter((s: any) => s.type === 'case_law').length
    return { eli, eurlex, caseLaw, total: sources.length }
  } catch {
    return { eli: 0, eurlex: 0, caseLaw: 0, total: 0 }
  }
}

function TurnCard({ turn, isLatest }: { turn: OpinionSessionTurnDetail; isLatest: boolean }) {
  const [expanded, setExpanded] = useState(isLatest)
  const typeInfo = getTypeLabel(turn.type)
  const TypeIcon = typeInfo.icon
  const sources = parseSources(turn.sourcesUsed)

  // Strip anonymization section for display
  const ANON_MARKER = '## \u{1F512} Anonimizacja danych osobowych'
  const anonIndex = turn.opinionText.indexOf(ANON_MARKER)
  const displayText = anonIndex !== -1
    ? turn.opinionText.substring(0, anonIndex).replace(/\n*---\n*$/, '')
    : turn.opinionText

  return (
    <div className={`border rounded-lg transition-colors ${isLatest ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50/50 rounded-t-lg transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 text-sm font-medium ${typeInfo.color}`}>
            <TypeIcon className="w-4 h-4" />
            <span>v{turn.version}</span>
            <span className="text-gray-400">-</span>
            <span>{typeInfo.label}</span>
          </div>
          {turn.type === 'Accepted' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <Check className="w-3 h-3" />
              Zaakceptowana
            </span>
          )}
          {isLatest && turn.type !== 'Accepted' && (
            <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
              Aktualna
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" />
            {formatDate(turn.createdAt)}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Sources bar */}
          {sources.total > 0 && (
            <div className="flex items-center gap-4 py-2 text-xs text-gray-500">
              {sources.eli > 0 && <span>ELI: {sources.eli}</span>}
              {sources.eurlex > 0 && <span>EUR-Lex: {sources.eurlex}</span>}
              {sources.caseLaw > 0 && <span>Orzecz.: {sources.caseLaw}</span>}
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3" />
                {turn.citationsVerified}/{turn.citationsTotal}
              </span>
            </div>
          )}

          {/* Opinion text */}
          <div className="prose max-w-none prose-sm mt-2">
            {isLatest ? (
              <ReactMarkdown>{displayText}</ReactMarkdown>
            ) : (
              <div>
                <p className="text-sm text-gray-700">
                  {displayText.substring(0, 300)}
                  {displayText.length > 300 && '...'}
                </p>
                {displayText.length > 300 && (
                  <button
                    onClick={(e) => { e.stopPropagation() }}
                    className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                  >
                    Pokaz pelna wersje
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function SessionTimeline({ turns, currentVersion }: SessionTimelineProps) {
  if (turns.length === 0) return null

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
        <Clock className="w-4 h-4" />
        Historia sesji ({turns.length} {turns.length === 1 ? 'wersja' : turns.length < 5 ? 'wersje' : 'wersji'})
      </h3>

      <div className="space-y-2">
        {turns.map((turn) => {
          const isLatest = turn.version === currentVersion

          return (
            <div key={turn.id}>
              {/* User feedback between turns */}
              {turn.userFeedback && (
                <div className="flex items-start gap-2 py-2 px-3 ml-4 mb-2">
                  <MessageSquare className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                    {turn.userFeedback}
                  </div>
                </div>
              )}

              <TurnCard turn={turn} isLatest={isLatest} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
